import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { useState } from 'react'
import { listOrders } from '@/api/orders'
import { KanbanCard } from '@/components/orders/KanbanCard'
import {
  KanbanCityFilter,
  KanbanCityFilterBanner,
} from '@/components/orders/KanbanCityFilter'
import { Button } from '@/components/ui/Button'
import { PillToggle } from '@/components/ui/PillToggle'
import { Loader } from '@/components/ui/Loader'
import { ARCHIVE_STATUSES, KANBAN_COLUMNS } from '@/utils/kanban-columns'
import { useUiStore } from '@/store/uiStore'
import { useIsMobile } from '@/hooks/useIsMobile'
import { PageHeader } from '@/components/layout/PageHeader'
import { useRoleOrderParams } from '@/hooks/useRoleOrderParams'
import type { Order } from '@/types/order'
import type { OrderStatus } from '@/types/enums'
function ordersInColumn(orders: Order[], statuses: OrderStatus[]) {
  const set = new Set(statuses)
  return orders.filter((o) => set.has(o.status as OrderStatus))
}

export function DashboardPage() {
  const { t } = useTranslation()
  const mobile = useIsMobile()
  const [selectedStage, setSelectedStage] = useState<string | null>(null)
  const showArchive = useUiStore((s) => s.showArchiveKanban)
  const setShowArchive = useUiStore((s) => s.setShowArchiveKanban)
  const cityFilter = useUiStore((s) => s.kanbanCityFilter)
  const setCityFilter = useUiStore((s) => s.setKanbanCityFilter)
  const roleParams = useRoleOrderParams()

  const { data, isLoading } = useQuery({
    queryKey: ['orders', 'dashboard', roleParams],
    queryFn: () => listOrders({ page: 1, pageSize: 500, ...roleParams }),
    // Реальный источник обновлений — `useOrdersRealtime` (подписка на
    // postgres_changes). При возврате фокуса/монтировании делаем
    // safety-refetch — на случай, если канал кратковременно моргнул.
    // `refetchInterval` убран намеренно: с ним канбан раз в 20с
    // перезапрашивал всю страницу и визуально «перезагружался» как от F5.
    refetchOnMount: 'always',
    refetchOnWindowFocus: 'always',
    staleTime: 5_000,
  })
  const allOrders = data?.items ?? []
  const isInitialLoading = isLoading && !data

  // Список городов для фильтра — из всех загруженных заказов (до фильтрации),
  // плюс уже выбранные (чтобы выбранный город не исчезал, если по нему сейчас
  // нет заказов в выборке).
  const availableCities = (() => {
    const set = new Set<string>()
    for (const o of allOrders) {
      const c = (o.address_city ?? '').trim()
      if (c) set.add(c)
    }
    for (const c of cityFilter) set.add(c)
    return [...set].sort((a, b) => a.localeCompare(b, 'ru'))
  })()

  // Клиентская фильтрация по городу: данные канбана уже загружены целиком.
  const orders =
    cityFilter.length === 0
      ? allOrders
      : allOrders.filter((o) =>
          cityFilter.includes((o.address_city ?? '').trim()),
        )

  const archiveOrders = orders.filter((o) =>
    ARCHIVE_STATUSES.includes(o.status as OrderStatus),
  )

  const activeCount = orders.filter((o) =>
    [
      'SEARCH',
      'ASSIGNED',
      'DRIVING',
      'ARRIVED',
      'INSPECTION',
      'APPRAISAL',
      'BUYING',
      'DOCUMENT_PREPARATION',
      'PAYMENT_TRANSFER',
      'TRANSPORTATION',
    ].includes(o.status),
  ).length

  function overdueInColumn(statuses: OrderStatus[]) {
    const sub = ordersInColumn(orders, statuses)
    return sub.filter((o) => {
      const base =
        o.status === 'SEARCH' ? o.created_at : o.status_updated_at
      return Date.now() - new Date(base).getTime() > 3600_000
    }).length
  }

  if (mobile && selectedStage) {
    const col = [
      ...KANBAN_COLUMNS,
      {
        id: 'archive',
        title: 'Архив',
        statuses: ARCHIVE_STATUSES as OrderStatus[],
      },
    ].find((c) => c.id === selectedStage)
    const list = col ? ordersInColumn(orders, col.statuses) : []
    return (
      <div>
        <div className="mb-4 flex items-center gap-2">
          <Button variant="outline" onClick={() => setSelectedStage(null)}>
            {t('orders.backArrow')}
          </Button>
          <h2 className="font-[family-name:var(--stryzh-font-family-space-grotesk)] text-[length:var(--stryzh-font-size-lg)] font-semibold">
            {col ? t(`orders.kanban.${col.id}`) : null}
          </h2>
          <span className="inline-flex min-h-[1.5rem] min-w-[1.5rem] items-center justify-center rounded-full bg-[color:var(--stryzh-color-accent-orange)] px-2 font-[family-name:var(--stryzh-font-family-mono)] text-[length:var(--stryzh-font-size-xs)] font-bold text-[color:var(--stryzh-color-text-on-orange-badge)]">
            {list.length}
          </span>
        </div>
        <div className="space-y-3">
          {isInitialLoading ? (
            <div className="py-8">
              <Loader centered />
            </div>
          ) : list.length === 0 ? (
            <p className="py-8 text-center text-sm text-[var(--stryzh-color-text-secondary)]">
              {t('common.noData')}
            </p>
          ) : (
            list.map((o) => <KanbanCard key={o.id} order={o} />)
          )}
        </div>
      </div>
    )
  }

  if (mobile && !selectedStage) {
    return (
      <div>
        <PageHeader
          title={t('dashboard.title')}
          actions={
            <div className="flex items-center gap-2">
              <KanbanCityFilter
                cities={availableCities}
                selected={cityFilter}
                onChange={setCityFilter}
              />
              <PillToggle
                checked={showArchive}
                onChange={setShowArchive}
                label={t('orders.archive')}
              />
            </div>
          }
        />
        <KanbanCityFilterBanner selected={cityFilter} onChange={setCityFilter} />
        <div className="space-y-2">
          {KANBAN_COLUMNS.map((col) => {
            const n = ordersInColumn(orders, col.statuses).length
            const od = overdueInColumn(col.statuses)
            return (
              <button
                key={col.id}
                type="button"
                className="flex w-full items-center justify-between rounded-xl border border-[var(--stryzh-color-border-default)] bg-[var(--stryzh-color-surface)] p-4 text-left"
                onClick={() => setSelectedStage(col.id)}
              >
                <span className="font-semibold">{t(`orders.kanban.${col.id}`)}</span>
                <span className="flex items-center gap-2">
                  {!isInitialLoading && od > 0 ? (
                    <span className="rounded-[var(--stryzh-radius-sm)] bg-[color:var(--stryzh-color-order-card-timeout-bg)] px-2 py-0.5 text-[length:var(--stryzh-font-size-xs)] text-[color:var(--stryzh-color-semantic-error)]">
                      {t('orders.overdue', { n: od })}
                    </span>
                  ) : null}
                  {isInitialLoading ? (
                    <span className="inline-flex min-h-[1.5rem] min-w-[2.5rem] items-center justify-center rounded-full bg-[color:var(--stryzh-color-surface-pressed)] px-2 text-xs text-[var(--stryzh-color-text-secondary)]">
                      …
                    </span>
                  ) : (
                    <span className="inline-flex min-h-[1.5rem] min-w-[1.5rem] items-center justify-center rounded-full bg-[color:var(--stryzh-color-accent-orange)] px-2 font-[family-name:var(--stryzh-font-family-mono)] text-[length:var(--stryzh-font-size-sm)] font-bold text-[color:var(--stryzh-color-text-on-orange-badge)]">
                      {n}
                    </span>
                  )}
                  <span className="text-[var(--stryzh-color-text-secondary)]">›</span>
                </span>
              </button>
            )
          })}
          {showArchive ? (
            <button
              type="button"
              className="flex w-full items-center justify-between rounded-xl border border-[var(--stryzh-color-border-default)] bg-[var(--stryzh-color-surface)] p-4 text-left"
              onClick={() => setSelectedStage('archive')}
            >
              <span className="font-semibold">{t('orders.archive')}</span>
              <span className="inline-flex min-h-[1.5rem] min-w-[1.5rem] items-center justify-center rounded-full bg-[color:var(--stryzh-color-accent-orange)] px-2 font-[family-name:var(--stryzh-font-family-mono)] text-[length:var(--stryzh-font-size-sm)] font-bold text-[color:var(--stryzh-color-text-on-orange-badge)]">
                {isInitialLoading ? '…' : archiveOrders.length}
              </span>
            </button>
          ) : null}
        </div>
      </div>
    )
  }

  return (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col">
      <PageHeader
        title={t('dashboard.title')}
        subtitle={
          isInitialLoading ? t('common.loading') : t('orders.activeSubtitle', { n: activeCount })
        }
        actions={
          <div className="flex items-center gap-2">
            <KanbanCityFilter
              cities={availableCities}
              selected={cityFilter}
              onChange={setCityFilter}
            />
            <PillToggle
              checked={showArchive}
              onChange={setShowArchive}
              label={t('orders.showArchive')}
            />
          </div>
        }
      />
      <KanbanCityFilterBanner selected={cityFilter} onChange={setCityFilter} />
      {isInitialLoading ? (
        <div className="flex min-h-0 flex-1 items-center justify-center pb-2">
          <Loader centered />
        </div>
      ) : (
        <div className="flex min-h-0 flex-1 gap-3 overflow-x-auto pb-2">
          {KANBAN_COLUMNS.map((col) => {
            const colOrders = ordersInColumn(orders, col.statuses)
            return (
              <div
                key={col.id}
                className="flex min-h-[min(100%,calc(100dvh-12rem))] min-w-[240px] flex-1 flex-col gap-2 rounded-xl border border-[var(--stryzh-color-border-default)] bg-[var(--stryzh-color-surface)] p-2"
              >
                <div className="flex shrink-0 items-center justify-between gap-2 px-1 pt-1">
                  <span className="font-[family-name:var(--stryzh-font-family-space-grotesk)] text-[length:var(--stryzh-font-size-lg)] font-semibold tracking-[var(--stryzh-letter-spacing-space-grotesk)] text-[color:var(--stryzh-color-text-primary)]">
                    {t(`orders.kanban.${col.id}`)}
                  </span>
                  <span className="inline-flex min-h-[1.5rem] min-w-[1.5rem] shrink-0 items-center justify-center rounded-full bg-[color:var(--stryzh-color-accent-orange)] px-2 font-[family-name:var(--stryzh-font-family-mono)] text-[length:var(--stryzh-font-size-xs)] font-bold text-[color:var(--stryzh-color-text-on-orange-badge)]">
                    {colOrders.length}
                  </span>
                </div>
                <div className="min-h-0 flex-1 space-y-2 overflow-y-auto px-0.5 pb-1 pt-[var(--stryzh-space-2)]">
                  {colOrders.length === 0 ? (
                    <div className="py-8 text-center text-xs text-[var(--stryzh-color-text-secondary)]">
                      {t('common.noData')}
                    </div>
                  ) : (
                    colOrders.map((o) => <KanbanCard key={o.id} order={o} />)
                  )}
                </div>
              </div>
            )
          })}
          {showArchive ? (
            <div className="flex min-h-[min(100%,calc(100dvh-12rem))] min-w-[240px] flex-1 flex-col gap-2 rounded-xl border border-[var(--stryzh-color-border-default)] bg-[var(--stryzh-color-surface)] p-2">
              <div className="flex shrink-0 items-center justify-between gap-2 px-1 pt-1">
                <span className="font-[family-name:var(--stryzh-font-family-space-grotesk)] text-[length:var(--stryzh-font-size-lg)] font-semibold tracking-[var(--stryzh-letter-spacing-space-grotesk)] text-[color:var(--stryzh-color-text-primary)]">
                  {t('orders.archive')}
                </span>
                <span className="inline-flex min-h-[1.5rem] min-w-[1.5rem] shrink-0 items-center justify-center rounded-full bg-[color:var(--stryzh-color-accent-orange)] px-2 font-[family-name:var(--stryzh-font-family-mono)] text-[length:var(--stryzh-font-size-xs)] font-bold text-[color:var(--stryzh-color-text-on-orange-badge)]">
                  {archiveOrders.length}
                </span>
              </div>
              <div className="min-h-0 flex-1 space-y-2 overflow-y-auto px-0.5 pb-1 pt-[var(--stryzh-space-2)]">
                {archiveOrders.length === 0 ? (
                  <div className="py-8 text-center text-xs text-[var(--stryzh-color-text-secondary)]">
                    {t('common.noData')}
                  </div>
                ) : (
                  archiveOrders.map((o) => <KanbanCard key={o.id} order={o} />)
                )}
              </div>
            </div>
          ) : null}
        </div>
      )}
    </div>
  )
}
